/**
 * 分析語彙 → GA4 のイベント名。gtag と dataLayer が共有する唯一の外部語彙定義。
 *
 * `book_` を冠するのは **GA4 が自動で採る `page_view` と構造的に衝突させない**ため。
 * 素の `page_view` で送ると、導入側が `send_page_view` をどう設定していても
 * ホストページの PV と誌面の PV が同じ指標に混ざる。名前を分ければ、導入側の
 * gtag 設定に一切依存せずに済む（我々は導入側の config を制御できない）。
 *
 * 機械的な文字列結合ではなく明示表にしてあるのは、`core/options.ts` の data 属性表と
 * 同じ理由 — 外から見える名前は推測で作らない。
 */
import type { AnalyticsEventName } from '../types';

export const GA4_NAMES: Readonly<Record<AnalyticsEventName, string>> = {
  page_view: 'book_page_view',
  page_leave: 'book_page_leave',
  zoom: 'book_zoom',
  overlay_click: 'book_overlay_click',
  search: 'book_search',
};

/**
 * 第三者へ渡さない値。**ここが外部送信の最後の関門**で、gtag と dataLayer の両方が通る。
 *
 * `search_term` は自前収集（同一サーバ）へは送るが、サーバ側は `E4B_EVENTS_KEEP_QUERY` が
 * 偽なら捨てる。同じ語を第三者へは黙って渡す、という非対称は同梱の「外部送信情報一覧」の
 * 記載と食い違うため、外部へは渡さない。GA4 に残るのは `hit_count` だけになる。
 */
const NOT_FOR_THIRD_PARTY: readonly string[] = ['search_term'];

/** GA4 へ渡すパラメータ（フラット 1 段）。 */
export function ga4Params(
  book: string,
  sid: string,
  page: number | undefined,
  value: Record<string, string | number | boolean> | undefined,
): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {
    book_id: book,
    session_id: sid,
  };
  if (page !== undefined) params.page_no = page;
  for (const [key, entry] of Object.entries(value ?? {})) {
    if (!NOT_FOR_THIRD_PARTY.includes(key)) params[key] = entry;
  }
  return params;
}
